<script setup>

// Imports
import { ref, computed, onMounted } from 'vue'

import Logo from './desktop/logo'
import LanguageSelector from './desktop/localeSelector'


// Props
const props = defineProps({
  navigation: {
    type: Array,
    default: () => []
  },
  additionalLanguages: {
    type: Array,
    default: () => []
  },
  siteHandle: {
    type: [String],
    default: ''
  },
  homeLink: {
    type: String,
    default: '/'
  },
  visibilityOptions: {
    type: String,
    default: ''
  },
  customHeaderCta: {
    type: Object,
    default: () => ({})
  },
  globalCtaSettings: {
    type: Object,
    default: () => ({})
  }
})

// Computed
// navItems: navigation items excluding buttons (typeHandle === 'navButton')
const navItems = computed(() => {
  const list = Array.isArray(props.navigation) ? props.navigation : []
  return list.filter(item => item?.typeHandle !== 'navButton')
})

// navButtons: navigation items that are buttons (typeHandle === 'navButton')
const navButtons = computed(() => {
  const list = Array.isArray(props.navigation) ? props.navigation : []
  return list.filter(item => item?.typeHandle === 'navButton')
})

const homeLink = computed(() => (typeof props.homeLink === 'string' && props.homeLink ? props.homeLink : '/'))
const globalCtaHasPopupOverride = computed(() => Boolean(props.globalCtaSettings?.popupEnabled))
const globalCtaPopupEnabled = computed(() => Boolean(props.globalCtaSettings?.popupEnabled))
const globalCtaTextOverride = computed(() => props.globalCtaSettings?.textOverride || '')

// Scroll tracking
const isScrolled = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 100
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  // Check initial scroll position
  handleScroll()
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <header :class="{ 'is-scrolled': isScrolled }">
    <div class="header-inner">
      <div class="columns container">
        

        <div class="left">
          <Logo :homeLink="homeLink" />
        </div>


        <div class="center"
          v-if="!['onlyTopBanner', 'hideEverything','noTopBannerNoNavigation', 'showTopBannerHideNavigation', 'showOnlyCta', 'showOnlyCtaWithoutLangSwitcher'].includes(visibilityOptions)"
        >
          <LayoutHeaderDesktopNavigation
              :navItems="navItems"
          />
        </div>

        <div class="right"
          v-if="!['onlyTopBanner', 'hideEverything', 'hideCta', 'hideCtasAndHideBanner'].includes(visibilityOptions) ||
            (customHeaderCta && customHeaderCta.url && customHeaderCta.title)"
        >

          <LanguageSelector
            v-if="!['showOnlyCtaWithoutLangSwitcher'].includes(visibilityOptions)"
            :additionalLanguages="additionalLanguages"
            :siteHandle="siteHandle"
            class="header-locale-selector"
          />

          <div v-if="customHeaderCta && customHeaderCta.url && customHeaderCta.title">
            <GlobalsButton
              :url="customHeaderCta.url"
              :title="customHeaderCta.title"
              class="custom-header-cta header-cta-button"
              :class="customHeaderCta.class"
            />
          </div>
          <div v-else-if="navButtons.length === 1"
              class="simple-button-container">
            <GlobalsButton v-for="button in navButtons"
              class="header-cta-button"
              :url="button?.linkField?.url"
              :title="globalCtaPopupEnabled && globalCtaTextOverride || button?.title"
              :popup-enabled="globalCtaPopupEnabled"
              bgColor="#000"
            />
          </div>

          <div v-else-if="navButtons.length > 1"
            class="multiple-buttons-container">

            <a v-if="!['showOnlyCta', 'showOnlyCtaWithoutLangSwitcher'].includes(visibilityOptions)"
              :href="navButtons[0]?.linkField?.url"
              target="_blank"
              rel="noopener noreferrer"
              class="transparent-button">

              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <mask id="mask0_7450_799" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                  <rect width="24" height="24" fill="#D9D9D9"/>
                </mask>
                <g mask="url(#mask0_7450_799)">
                  <circle style="transition: stroke 0.3s ease;" cx="12" cy="7.5" r="4.5" stroke="black" stroke-width="2"/>
                  <path style="transition: stroke 0.3s ease;" d="M12 12C7.33285 12 3.49543 15.9473 3.04434 21.0011C2.99524 21.5512 3.44772 22 4 22H20C20.5523 22 21.0048 21.5512 20.9557 21.0011C20.5046 15.9473 16.6672 12 12 12Z" stroke="black" stroke-width="2"/>
                </g>
              </svg>

              <span class="btn-text" style="transition: color 0.3s ease;">
                {{ navButtons[0]?.title }}
              </span>
            </a>

            <GlobalsButton class="header-cta-button"
              :url="navButtons[1]?.linkField?.url || '/'"
              :title="globalCtaPopupEnabled && globalCtaTextOverride || navButtons[1]?.title"
              :popup-enabled="globalCtaPopupEnabled"
              bgColor="#000"
            />

          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

header {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: var(--color-gray-150);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &.is-scrolled::before,
  &:has(a:hover)::before,
  &:has(.logo button:hover)::before,
  &:has(.locale-selector button:hover)::before,
  &:has(.dropdown-container)::before,
  &:has(.logo__list)::before,
  &:has(.locale-selector__list)::before {
    opacity: 1;
  }
}

.columns {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-gray-900);
}

.left {
  margin-right: 36px;
}

.center {
  flex-grow: 1;
}

.header-locale-selector {
  margin-inline: 10px;
}

.header-cta-button {
  height: 42px !important;
  padding-inline: 20px !important;
  min-width: 168px !important;
  margin-left: 24px !important;

  &:deep(.btn-text) {
    font-size: 16px !important;
    font-weight: 500 !important;
    letter-spacing: -.32px !important;
    line-height: 140% !important;
  }
}

.right {
  display: flex;
  align-items: center;
}

.multiple-buttons-container,
.transparent-button {
  display: flex;
  align-items: center;
}

.transparent-button {
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.2px;
  color: var(--color-black);
  padding-inline: 10px;
  margin-left: 8px;
  position: relative;
}

.btn-text {
  position: relative;
  margin-left: 4px;
}

.btn-text:after {
  content: '';
  position: absolute;
  bottom: -4px;
  height: 2px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-black);
  position: absolute;
  width: 0;
  transition: width 0.3s ease;
}

.transparent-button:hover .btn-text:after {
  width: 100%;
}

.header-cta-button.custom-header-cta {
  min-width: 121px !important;
  padding: 10px 20px !important;
}

.custom-header-cta.navy-blue :deep(.btn-bg) {
  background: #003F6B !important;
}

.custom-header-cta.blue-100 :deep(.btn-bg) {
  background: #114AA3 !important;
}

</style>

<!-- Global style to ensure it's not removed by critters -->
<style>
.page.--overlapping-header header.is-scrolled {
  height: 0;

  .header-inner {
    border-bottom: 1px solid var(--color-gray-150);
  }
}

.page.--overlapping-header .header-inner {
  border-bottom: 1px solid transparent;
}

.page.--overlapping-header .header-inner:has(.dropdown-container),
.page.--overlapping-header .header-inner:has(.left .logo.is-open),
.page.--overlapping-header .header-inner:has(.right .locale-selector.is-open),
.page.--overlapping-header .header-inner:has(.right .transparent-button:hover),
.page.--overlapping-header .header-inner:has(.right .header-cta-button:hover) {
  border-bottom: 1px solid var(--color-gray-150);
}

.page.--overlapping-header.--overlapping-header-dark .layout-header-desk:not(.is-scrolled) {

  .top-nav-item > a:focus-visible,
  .top-nav-item > button:focus-visible,
  .left .logo:focus-visible,
  .right .header-locale-selector button:focus-visible,
  .right .transparent-button:focus-visible,
  .right .header-cta-button:focus-visible {
    outline: 2px solid var(--color-white) !important;
  }
}

</style>
